import Collapse from '@mui/material/Collapse';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ampli } from '../../../../ampli';
import BlankImage from '../../../../assets/images/blank-image.png';
import { RiveAnimation } from '../../../../components/common/animations/RiveAnimation';
import { Button } from '../../../../components/common/buttons/Button';
import { Icon } from '../../../../components/common/icon/Icon';
import { ProductInquiryModal } from '../../../../components/custom/modal/product/ProductInquiryModal';
import { useHandleProductInquiry } from '../../../../hooks/product/useHandleProductInquiry';
import { useCheckUserLoggedIn } from '../../../../hooks/user/useCheckUserLoggedIn';
import { IProduct } from '../../../../interfaces/Product.interfaces';
import { isNewProduct } from '../../../../utils/calculator/calculateDate.util';
import { getGeneralMarketDetailPageURL } from '../../../../utils/getter/getProductDetailPageURL.util';
import { getGeneralProductPrice } from '../../../../utils/getter/getProductPrice.util';
import { PriceScreenSection } from '../../components/PriceScreenSection';
import { ProductChip } from '../components/ProductChip';
import { ProductPendingAlarmModal } from '../components/ProductPendingAlarmModal';
import { ProductStatusText } from '../components/ProductStatusText';
import { PriceReceiptInList } from './components/PriceReceiptInList';
import { ProductSeparatedThumbnailImage } from './components/ProductSeparatedThumbnailImage';

interface ProductVerticalListDetailViewProps {
  product: IProduct;
  category: 'PLUVA' | 'PARTNER';
}

// TODO: ProductDetilCard로 변경, UI와 비즈니스 로직 분리 필요 https://gint-team.atlassian.net/browse/PVM-1760
export const ProductVerticalListDetailView = ({ product, category }: ProductVerticalListDetailViewProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const [showProductPendingAlarmModal, setShowProductPendingAlarmModal] = useState<boolean>(false);
  const [isShowDetailPrice, setIsShowDetailPrice] = useState<boolean>(false);

  const [isProductInquiryModalOpen, setIsProductInquiryModalOpen] = useState<boolean>(false);

  const { handleProductInquiryButtonClick } = useHandleProductInquiry({
    product,
    category,
    page: 'PLP',
  });

  const price = useMemo<string>(() => {
    return getGeneralProductPrice({ product, isUserLoggedIn });
  }, [product]);

  const productDetailPageURL = useMemo<string>(() => {
    return getGeneralMarketDetailPageURL(product);
  }, [product.category]);

  const isSellingProduct = product.status === 'ONSALE' || product.status === 'IN_CONTRACT';

  return (
    <>
      <Container
        onClick={(e) => {
          e.stopPropagation();

          if (product.status === 'PENDING') {
            setShowProductPendingAlarmModal(true);
          } else if (product.status !== 'SOLDOUT') {
            navigate(productDetailPageURL);
          }
        }}
      >
        <ImageWrap id={'product-list-swiper-container'}>
          {product.status === 'PENDING' && <ProductStatusText text={'판매 예정'} />}
          {product.status === 'SOLDOUT' && <ProductStatusText text={'판매 완료'} />}
          {product.status === 'IN_CONTRACT' && <ProductStatusText text={'계약 진행 중'} />}

          {product.status === 'ONSALE' ? (
            <CustomSwiper
              slidesPerView={1}
              edgeSwipeDetection
              loop
              autoplay={{ delay: 3000 }}
              pagination={product.images.filter((image) => image.type === 'LIST').length > 1}
            >
              {product.images
                .filter((image) => image.type === 'LIST')
                .map((image) => (
                  <CustomSwiperSlide>
                    <ProductImage src={image.imageUrl} alt={image.id} onClick={() => {}} />
                    <div className={'swiper-lazy-preloader'} />
                  </CustomSwiperSlide>
                ))}
            </CustomSwiper>
          ) : (
            <Image
              src={
                product.images.filter((image) => image.type === 'LIST').length > 0
                  ? product.images.filter((image) => image.type === 'LIST')[0].imageUrl
                  : BlankImage
              }
              alt={
                product.images.filter((image) => image.type === 'LIST').length > 0
                  ? product.images.filter((image) => image.type === 'LIST')[0].imageUrl
                  : BlankImage
              }
              isBlurred={
                product.status === 'PENDING' || product.status === 'SOLDOUT' || product.status === 'IN_CONTRACT'
              }
            />
          )}

          <ProductSeparatedThumbnailImage productImages={product.images} />

          <BadgeContainer>
            <ProductChip type={category} />
            {isNewProduct(product.startOnSaleDate!, product.status) && <ProductChip type={'NEW'} />}
          </BadgeContainer>
        </ImageWrap>

        <InstallmentSection isBlurred={!isSellingProduct}>
          <InstallmentLeftSection>
            {isSellingProduct ? (
              <RiveAnimation name={'CircleWonAnimate'} size={'Large'} />
            ) : (
              <Icon name="CircleWon" size={'Large'} fill={'#445EFF'} />
            )}

            <InstallmentTitleWrapper>
              <InstallmentTitle fontWeight={700}>{'할부가 가능한 매물'}</InstallmentTitle>
              <InstallmentTitle fontWeight={500}>{'이예요!'}</InstallmentTitle>
            </InstallmentTitleWrapper>
          </InstallmentLeftSection>

          {/* TODO : 'PVM-1736 : 유저가 PLP 에 접속하여, 할부 문의 하기 버튼을 클릭할 수 있다' 작업에서 버튼 공동 컴포넌트로 구현 후 공통 컴포넌트로 교체 */}
          <Button
            icon={{ fileName: 'AngleRight', position: 'right', fill: '#FFFFFF', size: 'Small' }}
            borderType={'round'}
            color={'primary'}
            size={'xs'}
            text={{ size: 'sm', content: '할부 문의 하기' }}
            isDisabled={!isSellingProduct}
            onClick={() => {
              handleProductInquiryButtonClick();
              if (!isUserLoggedIn) {
                setIsProductInquiryModalOpen(true);
              }
            }}
          />
        </InstallmentSection>

        <ProductInfo isBlurred={!isSellingProduct}>
          <Title>{product.title}</Title>

          {isSellingProduct && (
            <>
              {product.metadata && (
                <MetadataContainer>
                  {product.metadata
                    .split(',')
                    .map((metadata) => metadata.trim())
                    .map((metadata, index, array) => {
                      return (
                        <>
                          <Metadata key={metadata}>{metadata}</Metadata>
                          {index !== array.length - 1 && <Metadata>{'·'}</Metadata>}
                        </>
                      );
                    })}
                </MetadataContainer>
              )}

              {(product.location || product.manufacturedYear || product.operationTime) && (
                <TagsContainer>
                  {product.location && (
                    <Tag>
                      <Icon name={'Marker'} width={14} height={14} />
                      <TagText>{product.location}</TagText>
                    </Tag>
                  )}

                  {product.manufacturedYear && (
                    <Tag>
                      <Icon name={'CalendarDaysColorRed'} size={'Medium'} />
                      <TagText>{`${product.manufacturedYear}년식`}</TagText>
                    </Tag>
                  )}

                  {product.operationTime && (
                    <Tag>
                      <Icon name={'ClockFiveDuotone'} size={'Medium'} />
                      <TagText>{`${product.operationTime.toLocaleString()}시간`}</TagText>
                    </Tag>
                  )}
                </TagsContainer>
              )}
            </>
          )}

          <PriceSection>
            <PriceTitleContainer
              onClick={(e) => {
                if (isSellingProduct) {
                  e.stopPropagation();
                  setIsShowDetailPrice((prev) => !prev);
                  ampli.마켓매물목록페이지가격더보기버튼클릭({
                    '게시글 제목': product.title,
                  });
                }
              }}
            >
              <PriceWrap>
                <Price>{price}</Price>

                {isSellingProduct && product.priceVisible === true && (
                  <DiscountWrap>
                    <Discount>{isUserLoggedIn ? '회원가' : '비회원가'}</Discount>
                  </DiscountWrap>
                )}
              </PriceWrap>

              {isSellingProduct &&
                product.priceVisible === true &&
                (isShowDetailPrice ? (
                  <Icon name={'ToggleOn'} width={10} height={10} />
                ) : (
                  <Icon name={'ToggleOff'} width={10} height={10} />
                ))}
            </PriceTitleContainer>
          </PriceSection>

          <Collapse in={isShowDetailPrice} style={{ width: '100%', position: 'relative' }}>
            {!isUserLoggedIn && <PriceScreenSection product={product} pageType={'plp'} />}
            <PriceReceiptInList product={product} />
          </Collapse>
        </ProductInfo>
      </Container>

      <ProductPendingAlarmModal
        isShow={showProductPendingAlarmModal}
        onClose={() => setShowProductPendingAlarmModal(false)}
        productTitle={product.title}
        isCaseIH={product.machine?.brand?.name?.includes('케이스')}
      />

      {isProductInquiryModalOpen && (
        <ProductInquiryModal product={product} page={'PLP'} onModalClose={() => setIsProductInquiryModalOpen(false)} />
      )}
    </>
  );
};

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  z-index: 1;

  gap: 16px;

  &:hover {
    cursor: pointer;
  }
`;

const BadgeContainer = styled.div`
  position: absolute;

  top: 10px;
  left: 10px;

  z-index: 50;

  display: flex;
  gap: 4px;
`;

const ImageWrap = styled.div`
  width: 100%;

  padding-top: 75%;

  position: relative;

  border-radius: 8px;

  background-color: #f2f3f5;
`;

const Image = styled.img<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  left: 0;
  top: 0;

  object-fit: cover;

  position: absolute;

  border-radius: 8px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const ProductInfo = styled.div<{ isBlurred: boolean }>`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;

  gap: 6px;

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const Title = styled.h1`
  color: var(--light-scheme-monochrome-black, #232429);
  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */

  overflow: hidden;
  text-overflow: ellipsis;

  display: block;
  white-space: nowrap;
  width: 100%;
`;

const PriceSection = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  gap: 16px;
`;

const PriceTitleContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PriceWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  gap: 4px;
`;

const Price = styled.span`
  color: var(--light-scheme-monochrome-black, #232429);
  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const DiscountWrap = styled.div`
  display: flex;
  align-items: center;
`;

const Discount = styled.span`
  color: var(--light-scheme-color-primary-red, #ff3049);
  /* Display/Small/s-bold */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 15.6px */
`;

const SrollSection = styled.div``;

const CustomSwiper = styled(Swiper)`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;
`;

const CustomSwiperSlide = styled(SwiperSlide)`
  width: 100%;
  height: 100%;

  border-radius: 8px;
  cursor: pointer;

  margin-bottom: 22px;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;

  object-fit: cover;

  border-radius: 8px;
  cursor: pointer;
`;

const TagsContainer = styled.div`
  display: flex;
  overflow: hidden;
  width: 100%;
  align-items: center;
  gap: var(--radius-medium, 8px);
`;

const Tag = styled.div`
  display: flex;
  padding: var(--radius-small, 4px) 6px;
  align-items: center;
  gap: 2px;

  border-radius: var(--radius-small, 4px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const TagText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */
`;

const MetadataContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-small, 4px);
`;

const Metadata = styled.div`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 130%; /* 18.2px */
`;

const InstallmentSection = styled.div<{ isBlurred: boolean }>`
  display: flex;
  padding: var(--radius-medium, 8px);
  justify-content: space-between;
  align-items: center;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-secondary-blue, #e5e9ff);

  opacity: ${(props) => (props.isBlurred ? 0.3 : 1)};
`;

const InstallmentLeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--radius-small, 4px);
`;

const InstallmentTitleWrapper = styled.div`
  display: flex;
`;

const InstallmentTitle = styled.span<{ fontWeight: number }>`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;

  overflow: hidden;
  color: var(--Light-Scheme-Color-primary-blue, #445eff);
  text-overflow: ellipsis;

  /* Display/Medium/m-bold */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: ${(props) => props.fontWeight};
  line-height: 130%; /* 18.2px */
`;

const InstallmentInquiryButton = styled.button`
  display: flex;
  padding: 6px var(--radius-medium, 8px);
  align-items: center;
  gap: 2px;

  border-radius: var(--radius-round, 80px);
  background: var(--Light-Scheme-Color-primary-blue, #445eff);
`;

const InstallmentInquiryButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);
  text-align: center;

  /* System/Small/s-medium */
  font-family: Pretendard;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 10.2px */
`;
